// "Cierres por periodo" in the Dashboard: how many closings the client had this week, this
// month and this year, each against the same stretch of the period before, and per week, month or
// year how its closings stand — stacked by state, the total over each bar and the share that
// reconciled as a line on its own axis, with the period's mix, its average and its best and worst
// period beneath. Opens by week. Periods before the first closing are not drawn.
//
// Read from `getCierreClosingDates`, the same answer the panel grouped by date draws its lines from,
// and counted with its own rule (`resumenDeFecha`), so a period here is the sum of the Panel's lines
// for its days. Pages of 100 dates, every one up to today, at most `MAX_PAGINAS` of them: the first
// says how many there are, the rest are asked together. Only the shell's client is ever drawn — an
// answer naming another client is dropped, never added to this one's.
import { useEffect, useMemo, useState } from "react";
import { Area, AreaChart, Bar, CartesianGrid, ComposedChart, LabelList, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ArrowDownRight, ArrowUpRight, CalendarCheck, CalendarDays, CalendarRange, Minus } from "lucide-react";
import { Spinner, cn } from "@mantis-sas/ui";

import { getCierreClosingDates, getExtractionFields } from "../../../ApiCalls/mantisBusiness";
import { getColombiaDateString } from "../../../utils/dateUtils";
import { ESTADOS_VOLUMEN, MODO, etiquetaDePeriodo, kpisDeVolumen, rangoDePeriodo, volumenPorPeriodo } from "../../../utils/cierreVolumen";
import { conReintento } from "../../../utils/conReintento";
import { guia } from "../../../utils/cierreTours";

const POR_PAGINA = 100;
const MAX_PAGINAS = 10;

// Sober tones of the platform's states: one family, none shouting over the others.
const COLOR_ESTADO = {
  ok: "#1f8a62",
  alerta: "#d94c4c",
  sinReportes: "#e2a33b",
  porRevisar: "#a3adbb",
};
const LINEA_CUADRE = "hsl(var(--primary))";
const EJE = { tickLine: false, axisLine: false, fontSize: 11, stroke: "var(--brinks-muted-foreground)" };

const MODOS = [
  { modo: MODO.SEMANA, etiqueta: "Semana" },
  { modo: MODO.MES, etiqueta: "Mes" },
  { modo: MODO.ANIO, etiqueta: "Año" },
];
const NOMBRE_PERIODO = { semana: "semana", mes: "mes", anio: "año" };

const ICONO_KPI = { semana: CalendarDays, mes: CalendarRange, anio: CalendarCheck };
const ETIQUETA_KPI = { semana: "Esta semana", mes: "Este mes", anio: "Este año" };
const COMPARA_KPI = { semana: "la semana pasada", mes: "el mes pasado", anio: "el año pasado" };

const numero = (n) => new Intl.NumberFormat("es-CO").format(n || 0);
const pct = (n, d) => (d ? Math.round((n / d) * 100) : null);

function IconoKpi({ modo }) {
  const Icono = ICONO_KPI[modo];
  return <Icono className="h-[15px] w-[15px]" />;
}

function Variacion({ kpi }) {
  if (kpi.variacion === null) {
    return <p className="truncate text-[11px] leading-tight text-brinks-muted-foreground">Sin cierres en {COMPARA_KPI[kpi.modo]}</p>;
  }
  const sube = kpi.variacion > 0;
  const igual = kpi.variacion === 0;
  const Icono = igual ? Minus : sube ? ArrowUpRight : ArrowDownRight;
  return (
    <p className="flex items-center gap-1.5 truncate text-[11px] leading-tight text-brinks-muted-foreground" title={`${numero(kpi.anterior)} cierres en el mismo tramo de ${COMPARA_KPI[kpi.modo]}`}>
      <span
        className={cn(
          "inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 font-semibold tabular-nums",
          igual ? "bg-brinks-surface text-brinks-muted-foreground" : sube ? "bg-brinks-success-soft text-brinks-success" : "bg-brinks-danger-soft text-brinks-danger"
        )}
      >
        <Icono className="h-3 w-3" />
        {igual ? "0%" : `${sube ? "+" : "-"}${Math.abs(kpi.variacion)}%`}
      </span>
      <span className="truncate">{`vs ${COMPARA_KPI[kpi.modo]}`}</span>
    </p>
  );
}

// The period, its total, the share that reconciled, and only the states it has.
function DetallePeriodo({ active, payload }) {
  const periodo = active && payload?.[0]?.payload;
  if (!periodo) return null;
  return (
    <div className="min-w-[200px] rounded-[12px] border bg-card px-3 py-2.5 text-[11.5px] shadow-brinks-card">
      <div className="mb-1.5 flex items-baseline justify-between gap-3">
        <p className="font-semibold text-brinks-foreground">{periodo.etiqueta}</p>
        <p className="tabular-nums text-brinks-muted-foreground">{numero(periodo.total)} cierres</p>
      </div>
      {periodo.cuadre !== null ? (
        <div className="mb-2">
          <div className="flex justify-between text-[10.5px] text-brinks-muted-foreground">
            <span>Cuadran</span>
            <span className="font-semibold tabular-nums text-brinks-foreground">{periodo.cuadre}%</span>
          </div>
          <div className="mt-0.5 h-1.5 overflow-hidden rounded-full bg-brinks-surface">
            <div className="h-full rounded-full" style={{ width: `${periodo.cuadre}%`, background: COLOR_ESTADO.ok }} />
          </div>
        </div>
      ) : null}
      {ESTADOS_VOLUMEN.filter((estado) => periodo[estado.clave] > 0).map((estado) => (
        <p key={estado.clave} className="flex items-center gap-2 py-0.5 tabular-nums text-brinks-foreground">
          <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: COLOR_ESTADO[estado.clave] }} />
          <span>{estado.etiqueta}</span>
          <span className="ml-auto">{numero(periodo[estado.clave])}</span>
          <span className="w-9 text-right text-brinks-muted-foreground">{pct(periodo[estado.clave], periodo.total)}%</span>
        </p>
      ))}
    </div>
  );
}

// One figure of the strip under the chart.
const Dato = ({ etiqueta, valor, detalle }) => (
  <div className="min-w-0 px-4 py-3">
    <p className="truncate text-[10.5px] font-medium uppercase tracking-[0.05em] text-brinks-muted-foreground">{etiqueta}</p>
    <p className="mt-0.5 truncate text-[16px] font-semibold tabular-nums text-brinks-foreground">{valor}</p>
    {detalle ? <p className="truncate text-[11px] text-brinks-muted-foreground">{detalle}</p> : null}
  </div>
);

// B-138 — `onPeriodo({ desde, hasta, etiqueta })`: a bar clicked sets the Dashboard's period to it.
export function VolumenCierres({ token, mantisBusinessId, clientCode, hoy, onPeriodo = null }) {
  const dia = hoy || getColombiaDateString();
  const [modo, setModo] = useState(MODO.SEMANA);
  const [dias, setDias] = useState(null);
  const [byState, setByState] = useState(null);
  const [error, setError] = useState(false);
  const [intento, setIntento] = useState(0);

  useEffect(() => {
    let activo = true;
    setDias(null);
    setError(false);
    // A page is asked again when the server was momentarily busy (a 503), not given up at once.
    const pagina = (n) =>
      conReintento(() => getCierreClosingDates(token, mantisBusinessId, clientCode, { until: dia, limit: POR_PAGINA, offset: n * POR_PAGINA }))
        // An answer for another client is not this one's, whatever it holds.
        .then((r) => (r?.success && (!r.data?.clientCode || r.data.clientCode === clientCode) ? r.data : null));
    (async () => {
      const primera = await pagina(0);
      if (!activo) return;
      if (!primera) {
        setError(true);
        return;
      }
      const fechas = primera.dates || [];
      const paginas = Math.min(MAX_PAGINAS, Math.ceil((primera.total || 0) / POR_PAGINA));
      const resto = fechas.length && paginas > 1 ? await Promise.all(Array.from({ length: paginas - 1 }, (_, i) => pagina(i + 1))) : [];
      if (!activo) return;
      if (resto.some((r) => !r)) {
        setError(true);
        return;
      }
      setDias([...fechas, ...resto.flatMap((r) => r.dates || [])]);
    })();
    return () => {
      activo = false;
    };
  }, [token, mantisBusinessId, clientCode, dia, intento]);

  // The client's own status words decide what "Cuadre OK" is, as in the panel.
  useEffect(() => {
    let activo = true;
    setByState(null);
    getExtractionFields(token, mantisBusinessId, clientCode)
      .then((campos) => {
        if (activo) setByState(campos?.statuses?.byState || null);
      })
      .catch(() => {});
    return () => {
      activo = false;
    };
  }, [token, mantisBusinessId, clientCode]);

  const kpis = useMemo(() => (dias ? kpisDeVolumen(dias, dia) : []), [dias, dia]);
  // Periods before the first one with closings are left out: they only push the chart aside.
  const periodos = useMemo(() => {
    if (!dias) return [];
    const todos = volumenPorPeriodo(dias, byState, modo, { hoy: dia }).map((p) => ({ ...p, cuadre: pct(p.ok, p.total) }));
    const primero = todos.findIndex((p) => p.total > 0);
    return primero < 0 ? todos : todos.slice(primero);
  }, [dias, byState, modo, dia]);
  // The KPI cards' small lines: the last periods of their own grouping.
  const series = useMemo(() => {
    if (!dias) return {};
    return Object.fromEntries([MODO.SEMANA, MODO.MES, MODO.ANIO].map((m) => [m, volumenPorPeriodo(dias, byState, m, { hoy: dia }).map((p) => ({ total: p.total }))]));
  }, [dias, byState, dia]);

  const resumen = useMemo(() => {
    const total = periodos.reduce((s, p) => s + p.total, 0);
    const conDatos = periodos.filter((p) => p.total > 0);
    const porEstado = ESTADOS_VOLUMEN.map((e) => ({ ...e, n: periodos.reduce((s, p) => s + p[e.clave], 0) }));
    const ordenados = [...conDatos].sort((a, b) => b.cuadre - a.cuadre);
    return {
      total,
      porEstado,
      cuadre: pct(porEstado[0].n, total),
      mejor: ordenados[0] || null,
      peor: ordenados.length > 1 ? ordenados[ordenados.length - 1] : null,
    };
  }, [periodos]);

  return (
    <section {...guia("Cierres por periodo", "Cuántos cierres hubo esta semana, este mes y este año, contra el mismo tramo del periodo anterior, y una barra por semana, mes o año. Toca una barra para ver la página en ese periodo.")} className="overflow-hidden rounded-[14px] border bg-card shadow-brinks-card" aria-label="Cierres por periodo">
      <header className="flex flex-wrap items-start justify-between gap-3 px-4 pb-2 pt-3.5">
        <div>
          <h2 className="text-[14px] font-semibold text-brinks-foreground">Cierres por periodo</h2>
          <p className="mt-0.5 text-[12px] text-brinks-muted-foreground">Cuántos cierres hubo, cómo quedaron y qué parte cuadró, por {NOMBRE_PERIODO[modo]}.{onPeriodo ? " Toca una barra para ver ese periodo en el Dashboard." : ""}</p>
        </div>
        <div className="flex items-center gap-0.5 rounded-[9px] border bg-card p-0.5" role="group" aria-label="Agrupar por">
          {MODOS.map((m) => (
            <button
              key={m.modo}
              type="button"
              aria-pressed={modo === m.modo}
              onClick={() => setModo(m.modo)}
              className={cn(
                "rounded-[7px] px-3 py-1 text-[12px] font-medium transition",
                modo === m.modo ? "bg-primary text-primary-foreground" : "text-brinks-muted-foreground hover:text-brinks-foreground"
              )}
            >
              {m.etiqueta}
            </button>
          ))}
        </div>
      </header>

      <div className="px-4 pb-4">
        {error ? (
          <div role="alert" className="grid place-items-center gap-2 rounded-[10px] border border-dashed py-10 text-center">
            <p className="text-[12.5px] text-brinks-danger">No se pudieron cargar los cierres.</p>
            <button type="button" onClick={() => setIntento((n) => n + 1)} className="rounded-[8px] border bg-card px-3 py-1.5 text-[12px] font-medium text-brinks-foreground hover:bg-brinks-surface-hover">
              Reintentar
            </button>
          </div>
        ) : !dias ? (
          <div className="grid place-items-center py-14">
            <Spinner size="lg" />
          </div>
        ) : !dias.length ? (
          <div className="grid place-items-center rounded-[10px] border border-dashed py-10 text-center">
            <div>
              <p className="text-[13px] font-medium text-brinks-foreground">Sin cierres todavía</p>
              <p className="text-[12px] text-brinks-muted-foreground">Se llena a medida que corren cierres reales.</p>
            </div>
          </div>
        ) : (
          <>
            <div className="mb-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
              {kpis.map((kpi) => (
                <article key={kpi.modo} className="flex min-w-0 items-center gap-3 rounded-[12px] border bg-brinks-surface/40 px-3 py-2.5">
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-[10px] bg-card text-brinks-foreground shadow-brinks-card">
                    <IconoKpi modo={kpi.modo} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[20px] font-semibold leading-tight tabular-nums text-brinks-foreground">{numero(kpi.actual)}</p>
                    <p className="truncate text-[11.5px] leading-tight text-brinks-muted-foreground">{ETIQUETA_KPI[kpi.modo]}</p>
                    <div className="mt-1">
                      <Variacion kpi={kpi} />
                    </div>
                  </div>
                  <div className="h-10 w-20 shrink-0" aria-hidden="true">
                    {(series[kpi.modo] || []).length > 1 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={series[kpi.modo]} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
                          <Area type="monotone" dataKey="total" stroke={LINEA_CUADRE} strokeWidth={1.5} fill="rgba(15,23,42,0.06)" isAnimationActive={false} />
                        </AreaChart>
                      </ResponsiveContainer>
                    ) : null}
                  </div>
                </article>
              ))}
            </div>

            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={periodos}
                  margin={{ top: 22, right: 4, bottom: 0, left: 0 }}
                  barCategoryGap="28%"
                  style={onPeriodo ? { cursor: "pointer" } : undefined}
                  onClick={(e) => {
                    const p = e?.activePayload?.[0]?.payload;
                    if (!onPeriodo || !p?.total) return;
                    onPeriodo({ ...rangoDePeriodo(p.clave, modo), etiqueta: etiquetaDePeriodo(p.clave, modo) });
                  }}
                >
                  <CartesianGrid stroke="var(--brinks-border)" vertical={false} />
                  <XAxis dataKey="etiqueta" {...EJE} interval={0} />
                  <YAxis yAxisId="n" allowDecimals={false} tickFormatter={numero} {...EJE} width={44} />
                  <YAxis yAxisId="pct" orientation="right" domain={[0, 100]} tickFormatter={(v) => `${v}%`} {...EJE} width={40} />
                  <Tooltip content={<DetallePeriodo />} cursor={{ fill: "var(--brinks-surface)", opacity: 0.6 }} />
                  {ESTADOS_VOLUMEN.map((estado, i) => (
                    <Bar
                      key={estado.clave}
                      yAxisId="n"
                      dataKey={estado.clave}
                      name={estado.etiqueta}
                      stackId="volumen"
                      fill={COLOR_ESTADO[estado.clave]}
                      maxBarSize={46}
                      radius={i === ESTADOS_VOLUMEN.length - 1 ? [6, 6, 0, 0] : [0, 0, 0, 0]}
                    >
                      {i === ESTADOS_VOLUMEN.length - 1 ? (
                        <LabelList dataKey="total" position="top" formatter={(v) => (v ? numero(v) : "")} fontSize={11} fill="var(--brinks-foreground)" />
                      ) : null}
                    </Bar>
                  ))}
                  <Line
                    yAxisId="pct"
                    type="monotone"
                    dataKey="cuadre"
                    name="% que cuadra"
                    stroke={LINEA_CUADRE}
                    strokeWidth={2}
                    dot={{ r: 3.5, fill: "#ffffff", strokeWidth: 2 }}
                    activeDot={{ r: 5 }}
                    connectNulls
                    isAnimationActive={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            <div className="mt-3 overflow-hidden rounded-[12px] border">
              <div className="flex h-2 w-full overflow-hidden bg-brinks-surface" aria-hidden="true">
                {resumen.porEstado.map((e) => (resumen.total ? <span key={e.clave} style={{ width: `${(e.n / resumen.total) * 100}%`, background: COLOR_ESTADO[e.clave] }} /> : null))}
              </div>
              <div className="grid grid-cols-2 divide-x divide-y sm:grid-cols-4 sm:divide-y-0 xl:grid-cols-7">
                {resumen.porEstado.map((e) => (
                  <div key={e.clave} className="min-w-0 px-4 py-3">
                    <p className="flex items-center gap-1.5 truncate text-[10.5px] font-medium uppercase tracking-[0.05em] text-brinks-muted-foreground">
                      <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: COLOR_ESTADO[e.clave] }} />
                      {e.etiqueta}
                    </p>
                    <p className="mt-0.5 text-[16px] font-semibold tabular-nums text-brinks-foreground">{numero(e.n)}</p>
                    <p className="text-[11px] tabular-nums text-brinks-muted-foreground">{pct(e.n, resumen.total) ?? 0}% del total</p>
                  </div>
                ))}
                <Dato etiqueta="Cuadre promedio" valor={resumen.cuadre === null ? "—" : `${resumen.cuadre}%`} detalle={`${numero(resumen.total)} cierres`} />
                <Dato etiqueta={`Mejor ${NOMBRE_PERIODO[modo]}`} valor={resumen.mejor ? `${resumen.mejor.cuadre}%` : "—"} detalle={resumen.mejor?.etiqueta} />
                <Dato etiqueta={`Peor ${NOMBRE_PERIODO[modo]}`} valor={resumen.peor ? `${resumen.peor.cuadre}%` : "—"} detalle={resumen.peor?.etiqueta} />
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
